/**
 * InsightCard Component - Unified AI Insight Display
 * Consolidates AIInsightPanel, ContextualNarrativePanel, and NarrativeCard
 * Provides consistent, readable, and actionable insight presentation
 */

import { useState, useMemo, ReactNode } from 'react'
import clsx from 'clsx'
import {
  Brain,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Zap,
  Target,
  Shield,
  Activity
} from 'lucide-react'
import {
  parseNarrative,
  structureNarrative,
  detectSeverity,
  estimateReadingTime,
  truncateText,
  formatDisplayNumber,
  type FormattedSegment
} from '@/utils/textFormatting'

// ============================================================================
// TYPES
// ============================================================================

export type InsightVariant = 'compact' | 'standard' | 'detailed' | 'executive'
export type InsightType = 'analysis' | 'prediction' | 'recommendation' | 'alert' | 'summary'
export type InsightSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'success' | 'warning'

export interface InsightMetric {
  label: string
  value: string | number
  unit?: string
  trend?: 'up' | 'down' | 'stable'
  status?: 'good' | 'warning' | 'critical' | 'neutral'
  target?: number
}

export interface InsightAction {
  id: string
  label: string
  description?: string
  priority?: 'immediate' | 'soon' | 'planned'
  onClick?: () => void
  href?: string
}

export interface InsightCardProps {
  // Core content
  title: string
  narrative: string
  type?: InsightType
  severity?: InsightSeverity
  confidence?: number

  // Variant controls display density
  variant?: InsightVariant

  // Optional structured data
  metrics?: InsightMetric[]
  actions?: InsightAction[]
  keyPoints?: string[]

  // Context
  assetId?: string
  modelType?: 'foundation' | 'base' | 'per_asset' | 'ensemble'
  timestamp?: string
  source?: string

  // Behavior
  expandable?: boolean
  defaultExpanded?: boolean
  showReadingTime?: boolean
  showCopyButton?: boolean

  // Callbacks
  onActionClick?: (action: InsightAction) => void
  onExpand?: (expanded: boolean) => void

  className?: string
  children?: ReactNode
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const TYPE_CONFIG = {
  analysis: {
    icon: Brain,
    label: 'Analysis',
    color: 'text-primary-400',
    bgColor: 'bg-primary-500/10',
    borderColor: 'border-primary-500/30'
  },
  prediction: {
    icon: TrendingUp,
    label: 'Prediction',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30'
  },
  recommendation: {
    icon: Lightbulb,
    label: 'Recommendation',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30'
  },
  alert: {
    icon: AlertTriangle,
    label: 'Alert',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30'
  },
  summary: {
    icon: Activity,
    label: 'Summary',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30'
  }
}

const SEVERITY_CONFIG = {
  critical: {
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/40',
    badge: 'bg-red-500/20 text-red-400 border-red-500/30',
    pulse: true
  },
  high: {
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    pulse: false
  },
  warning: {
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    pulse: false
  },
  medium: {
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    badge: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    pulse: false
  },
  low: {
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    pulse: false
  },
  info: {
    color: 'text-surface-400',
    bgColor: 'bg-surface-800',
    borderColor: 'border-surface-700',
    badge: 'bg-surface-700 text-surface-400 border-surface-600',
    pulse: false
  },
  success: {
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    pulse: false
  }
}

const MODEL_LABELS = {
  foundation: 'Foundation Model',
  base: 'Base Model',
  per_asset: 'Per-Asset Model',
  ensemble: 'Ensemble Model'
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Renders formatted narrative segments with semantic highlighting */
function FormattedNarrative({ segments }: { segments: FormattedSegment[] }) {
  return (
    <span>
      {segments.map((segment, index) => {
        switch (segment.type) {
          case 'percentage':
          case 'number':
            return (
              <span key={index} className="font-mono font-semibold text-primary-400">
                {segment.content}
              </span>
            )
          case 'time':
            return (
              <span key={index} className="font-medium text-cyan-400">
                {segment.content}
              </span>
            )
          case 'asset':
            return (
              <span key={index} className="font-mono text-xs px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400 mx-0.5">
                {segment.content}
              </span>
            )
          case 'status':
            return (
              <span
                key={index}
                className={clsx(
                  'font-semibold',
                  segment.status === 'critical' && 'text-red-400',
                  segment.status === 'warning' && 'text-amber-400',
                  segment.status === 'good' && 'text-emerald-400'
                )}
              >
                {segment.content}
              </span>
            )
          case 'action':
            return (
              <span key={index} className="font-medium text-cyan-300 underline decoration-dotted underline-offset-2">
                {segment.content}
              </span>
            )
          default:
            return <span key={index}>{segment.content}</span>
        }
      })}
    </span>
  )
}

/** Metric display pill */
function MetricPill({ metric }: { metric: InsightMetric }) {
  const TrendIcon = metric.trend === 'up' ? TrendingUp : metric.trend === 'down' ? TrendingDown : Minus

  const statusColors = {
    good: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
    warning: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
    critical: 'text-red-400 border-red-500/30 bg-red-500/10',
    neutral: 'text-surface-300 border-surface-600 bg-surface-800'
  }

  return (
    <div className={clsx(
      'flex flex-col items-center p-3 rounded-lg border min-w-[80px]',
      statusColors[metric.status || 'neutral']
    )}>
      <span className="text-[10px] uppercase tracking-wider text-surface-400 mb-1">
        {metric.label}
      </span>
      <span className="text-lg font-mono font-bold">
        {typeof metric.value === 'number' ? formatDisplayNumber(metric.value) : metric.value}
        {metric.unit && <span className="text-xs ml-0.5">{metric.unit}</span>}
      </span>
      {metric.trend && (
        <div className={clsx(
          'flex items-center gap-0.5 text-xs mt-1',
          metric.trend === 'up' ? 'text-emerald-400' :
          metric.trend === 'down' ? 'text-red-400' : 'text-surface-500'
        )}>
          <TrendIcon size={12} />
          <span>{metric.trend}</span>
        </div>
      )}
    </div>
  )
}

/** Confidence indicator */
function ConfidenceBadge({ confidence }: { confidence: number }) {
  const percent = Math.round(confidence * 100)
  const color = percent >= 85 ? 'text-emerald-400 bg-emerald-500/20' :
                percent >= 70 ? 'text-amber-400 bg-amber-500/20' :
                'text-red-400 bg-red-500/20'

  return (
    <div className={clsx('flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium', color)}>
      <Target size={12} />
      <span>{percent}% confident</span>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function InsightCard({
  title,
  narrative,
  type = 'analysis',
  severity,
  confidence,
  variant = 'standard',
  metrics = [],
  actions = [],
  keyPoints = [],
  assetId,
  modelType,
  timestamp,
  source,
  expandable = true,
  defaultExpanded = false,
  showReadingTime = false,
  showCopyButton = true,
  onActionClick,
  onExpand,
  className,
  children
}: InsightCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [copied, setCopied] = useState(false)

  // Auto-detect severity from narrative if not provided
  const effectiveSeverity = severity || detectSeverity(narrative)
  const typeConfig = TYPE_CONFIG[type]
  const severityConfig = SEVERITY_CONFIG[effectiveSeverity]
  const TypeIcon = typeConfig.icon

  // Parse narrative for rich formatting
  const parsedNarrative = useMemo(() => parseNarrative(narrative), [narrative])
  const structuredContent = useMemo(() => structureNarrative(narrative), [narrative])
  const readingTime = useMemo(() => estimateReadingTime(narrative), [narrative])

  // Use extracted key points if none provided
  const displayKeyPoints = keyPoints.length > 0 ? keyPoints : structuredContent.keyPoints
  const displayMetrics = metrics.length > 0 ? metrics : structuredContent.metrics.map(m => ({
    label: m.label,
    value: m.value,
    unit: m.unit,
    status: m.status
  }))

  const handleExpand = () => {
    const newState = !isExpanded
    setIsExpanded(newState)
    onExpand?.(newState)
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(narrative)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Determine if we should show truncated content
  const isLongNarrative = narrative.length > 200
  const displayNarrative = variant === 'compact' && !isExpanded
    ? truncateText(narrative, 150)
    : narrative

  return (
    <div className={clsx(
      'rounded-xl border overflow-hidden transition-all',
      severityConfig.borderColor,
      severityConfig.bgColor,
      severityConfig.pulse && 'animate-pulse-slow',
      className
    )}>
      {/* Header */}
      <div className="p-4 border-b border-surface-700/50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {/* Type Icon */}
            <div className={clsx('p-2 rounded-lg flex-shrink-0', typeConfig.bgColor)}>
              <TypeIcon size={18} className={typeConfig.color} />
            </div>

            <div className="min-w-0 flex-1">
              {/* Title Row */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-surface-100 text-sm truncate">{title}</h3>

                {/* Severity Badge */}
                {effectiveSeverity !== 'info' && (
                  <span className={clsx(
                    'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
                    severityConfig.badge
                  )}>
                    {effectiveSeverity}
                  </span>
                )}

                {/* Asset ID */}
                {assetId && (
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400">
                    {assetId}
                  </span>
                )}
              </div>

              {/* Meta Row */}
              <div className="flex items-center gap-3 mt-1 text-xs text-surface-500 flex-wrap">
                {modelType && (
                  <span className="flex items-center gap-1">
                    <Brain size={11} />
                    {MODEL_LABELS[modelType]}
                  </span>
                )}
                {timestamp && (
                  <span className="flex items-center gap-1">
                    <Clock size={11} />
                    {timestamp}
                  </span>
                )}
                {showReadingTime && (
                  <span>{readingTime}</span>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Confidence & Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {confidence !== undefined && (
              <ConfidenceBadge confidence={confidence} />
            )}

            {showCopyButton && (
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-700 transition-colors"
                title="Copy insight"
              >
                {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            )}

            {expandable && isLongNarrative && (
              <button
                onClick={handleExpand}
                className="p-1.5 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-700 transition-colors"
              >
                <ChevronDown
                  size={16}
                  className={clsx('transition-transform', isExpanded && 'rotate-180')}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Bar - Always visible for standard/detailed variants */}
      {displayMetrics.length > 0 && variant !== 'compact' && (
        <div className="px-4 py-3 border-b border-surface-700/50 bg-surface-900/30">
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            {displayMetrics.slice(0, variant === 'detailed' ? 6 : 4).map((metric, index) => (
              <MetricPill key={index} metric={metric} />
            ))}
          </div>
        </div>
      )}

      {/* Narrative Content */}
      <div className="p-4">
        <p className="text-sm text-surface-300 leading-relaxed">
          {variant === 'compact' && !isExpanded ? (
            displayNarrative
          ) : (
            <FormattedNarrative segments={parsedNarrative} />
          )}
        </p>

        {/* Key Points - Show on expand or for detailed variant */}
        {(isExpanded || variant === 'detailed') && displayKeyPoints.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-xs font-semibold text-surface-400 uppercase flex items-center gap-1.5">
              <Zap size={12} />
              Key Points
            </h4>
            <ul className="space-y-1.5">
              {displayKeyPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                  <span className="text-primary-400 mt-1">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        {actions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-surface-700/50">
            <h4 className="text-xs font-semibold text-surface-400 uppercase flex items-center gap-1.5 mb-3">
              <Shield size={12} />
              Recommended Actions
            </h4>
            <div className="space-y-2">
              {actions.map((action) => {
                const priorityColors = {
                  immediate: 'bg-red-500/10 border-red-500/30 text-red-400',
                  soon: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
                  planned: 'bg-surface-800 border-surface-600 text-surface-300'
                }
                const color = priorityColors[action.priority || 'planned']

                return (
                  <button
                    key={action.id}
                    onClick={() => {
                      action.onClick?.()
                      onActionClick?.(action)
                    }}
                    className={clsx(
                      'w-full flex items-center justify-between p-3 rounded-lg border transition-all hover:scale-[1.01]',
                      color
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {action.priority && (
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-current/20">
                          {action.priority}
                        </span>
                      )}
                      <div className="text-left">
                        <span className="text-sm font-medium">{action.label}</span>
                        {action.description && (
                          <p className="text-xs text-surface-500 mt-0.5">{action.description}</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-surface-500" />
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Children slot for additional content */}
        {children}
      </div>

      {/* Footer - Source attribution */}
      {source && (
        <div className="px-4 py-2 bg-surface-900/50 border-t border-surface-700/50 flex items-center justify-between text-xs text-surface-500">
          <span className="flex items-center gap-1">
            <Activity size={11} />
            Source: {source}
          </span>
          {timestamp && (
            <span>Generated {timestamp}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// VARIANT EXPORTS
// ============================================================================

/** Compact insight for dashboard grids */
export function CompactInsight(props: Omit<InsightCardProps, 'variant'>) {
  return <InsightCard {...props} variant="compact" showReadingTime={false} />
}

/** Standard insight for general use */
export function StandardInsight(props: Omit<InsightCardProps, 'variant'>) {
  return <InsightCard {...props} variant="standard" />
}

/** Detailed insight for deep analysis */
export function DetailedInsight(props: Omit<InsightCardProps, 'variant'>) {
  return <InsightCard {...props} variant="detailed" defaultExpanded />
}

/** Executive summary style */
export function ExecutiveInsight(props: Omit<InsightCardProps, 'variant'>) {
  return <InsightCard {...props} variant="executive" showReadingTime />
}
